import React from 'react';
import {
  ArrowDownLeft,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Wallet,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { cn } from '../../lib/utils';
import { formatInr } from '../../utils/reconciliation/formatters';

function Truncate({ text, max = 28 }) {
  const str = text || '—';
  if (str.length <= max) return <span>{str}</span>;
  return (
    <span title={str} className="cursor-help truncate block max-w-[180px]">
      {str.slice(0, max - 1)}…
    </span>
  );
}

function TypeIcon({ type }) {
  const isCredit = String(type).toLowerCase() === 'credit';
  const Icon = isCredit ? ArrowDownLeft : ArrowUpRight;
  return (
    <span
      className={cn(
        'inline-flex h-7 w-7 items-center justify-center rounded-lg border',
        isCredit
          ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
          : 'border-rose-500/30 bg-rose-500/10 text-rose-400',
      )}
    >
      <Icon className="h-3.5 w-3.5" />
    </span>
  );
}

function ChannelIcon({ channel }) {
  if (channel === 'wallet_recharge') return <Wallet className="h-3 w-3 text-sky-400" />;
  if (channel === 'refund') return <RotateCcw className="h-3 w-3 text-amber-400" />;
  return null;
}

function statusVariant(status) {
  if (status === 'failed') return 'danger';
  if (status === 'reversed') return 'warning';
  return 'success';
}

function categoryVariant(category) {
  if (category === 'client') return 'success';
  if (category === 'owner') return 'warning';
  if (category === 'treasury') return 'default';
  return 'muted';
}

export default function ReconciliationTable({
  rows = [],
  page = 1,
  pageSize = 25,
  onPageChange,
  totalLoaded,
  hasMore,
}) {
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const pageRows = rows.slice(start, start + pageSize);

  return (
    <div className="recon-table-shell">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-de-gold/15 px-4 py-3 sm:px-5">
        <p className="m-0 text-sm font-bold text-de-gold">Ledger entries</p>
        <p className="m-0 text-[10px] text-de-muted">
          {rows.length} filtered · {totalLoaded} loaded
          {hasMore ? ' · more available' : ''}
        </p>
      </div>

      <p className="recon-table-scroll-hint m-0 px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-de-gold/70 lg:hidden">
        Swipe horizontally for full ledger columns
      </p>

      <div className="recon-table-scroll recon-table-scroll-premium">
        <Table className="recon-premium-table min-w-[1060px]">
          <TableHeader className="recon-table-head">
            <TableRow className="border-de-gold/20 hover:bg-transparent">
              <TableHead className="recon-sticky-col w-10" />
              <TableHead className="recon-sticky-col recon-sticky-col-2 whitespace-nowrap">Date</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Machine</TableHead>
              <TableHead>Channel</TableHead>
              <TableHead className="text-right">Credit</TableHead>
              <TableHead className="text-right">Debit</TableHead>
              <TableHead className="text-right">GST</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.length > 0 ? (
              pageRows.map((row, i) => (
                <TableRow
                  key={row.id || `${row.receiptRef}-${i}`}
                  className={cn(
                    'recon-table-row border-de-gold/10 transition-colors',
                    i % 2 === 1 && 'recon-table-row-zebra',
                  )}
                >
                  <TableCell className="recon-sticky-col w-10">
                    <TypeIcon type={row.type} />
                  </TableCell>
                  <TableCell className="recon-sticky-col recon-sticky-col-2 whitespace-nowrap tabular-nums text-de-muted">
                    {row.displayDate}
                  </TableCell>
                  <TableCell>
                    <Badge variant={categoryVariant(row.category)}>{row.categoryLabel || 'Internal'}</Badge>
                  </TableCell>
                  <TableCell className="font-semibold text-de-gold">
                    <Truncate text={row.receiptRef} max={22} />
                  </TableCell>
                  <TableCell className="max-w-[160px]">
                    <Truncate text={row.clientName} max={24} />
                  </TableCell>
                  <TableCell className="max-w-[140px]">
                    <Truncate text={row.clientCompany} max={20} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-slate-400">
                    {row.machineCode || '—'}
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5 text-xs text-slate-300">
                      <ChannelIcon channel={row.channel} />
                      {row.channelLabel}
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-emerald-400">
                    {row.credit ? formatInr(row.credit) : '—'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-rose-400">
                    {row.debit ? formatInr(row.debit) : '—'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-de-muted">{formatInr(row.gst)}</TableCell>
                  <TableCell className="text-right tabular-nums font-bold text-de-gold">{formatInr(row.totalInclGst)}</TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(row.status)}>{row.statusLabel}</Badge>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={13} className="py-12 text-center text-de-muted">
                  No transactions match your filters
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {rows.length > pageSize && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-de-gold/15 px-4 py-3 sm:px-5">
          <p className="m-0 text-[10px] text-de-muted">
            Page {safePage} of {totalPages} · showing {start + 1}–{Math.min(start + pageSize, rows.length)}
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="recon-page-btn"
              disabled={safePage <= 1}
              onClick={() => onPageChange(safePage - 1)}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="recon-page-btn"
              disabled={safePage >= totalPages}
              onClick={() => onPageChange(safePage + 1)}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
